"use client";

import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
export default function Cybersecurity() {
  const prefersReducedMotion = useReducedMotion();
  const fadeUp = prefersReducedMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  };
  const cards = [{
    key: "threats",
    eyebrow: "24/7 Monitoring",
    title: "Threat Defense",
    description: "Proactive security protocols designed to neutralize digital threats before they impact your critical infrastructure.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/cyber_threat_shield.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/cyber_threat_shield.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/cyber_threat_shield.webp-wvc-sizes",
      alt: "Digital shield icon representing advanced network security and threat protection"
    },
    href: "/CybersecurityCyberGenixPage"
  }, {
    key: "encryption",
    eyebrow: "AES-256 Standard",
    title: "Data Encryption",
    description: "Military-grade encryption services ensuring your sensitive communications remain private and inaccessible to intruders.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/cyber_data_lock.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/cyber_data_lock.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/cyber_data_lock.png-wvc-sizes",
      alt: "Abstract visualization of encrypted data streams secured by a digital padlock"
    },
    href: "/CybersecurityCyberGenixPage"
  }, {
    key: "compliance",
    eyebrow: "Audit Ready",
    title: "Compliance",
    description: "Comprehensive security audits and regulatory alignment to keep your business operations fully compliant and secure.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/cyber_audit_report.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/cyber_audit_report.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/cyber_audit_report.webp-wvc-sizes",
      alt: "Professional security audit report displayed on a secure digital dashboard"
    },
    href: "/CybersecurityCyberGenixPage"
  }];
  return <section data-section-id="27652" id="cybersecuritybestpracticestraining" data-nav="light" className="relative bg-background text-foreground py-32 md:py-44">
      {}
      <div aria-hidden="true" className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-24" style={{
      backgroundColor: "var(--primary)"
    }} />

      <div className="mx-auto w-full max-w-[1360px] px-5 sm:px-8 lg:px-12">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-8">
          <motion.div {...fadeUp} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.7,
          ease: [0.2, 0.6, 0.2, 1]
        }} className="lg:col-span-7">
            <p className="font-sans italic uppercase text-xs md:text-sm mb-6" style={{
            color: "var(--primary)",
            letterSpacing: "0.14em",
            fontVariant: "small-caps"
          }}>
              <span aria-hidden="true" className="inline-block align-middle h-px w-6 mr-3" style={{
              backgroundColor: "var(--primary)"
            }} />
              Cybersecurity Training
            </p>
            <h2 className="font-sans text-foreground" style={{
            fontSize: "clamp(2rem, 4.2vw, 4rem)",
            fontWeight: 600,
            letterSpacing: "-0.015em",
            lineHeight: 1.08
          }}>
              Advanced Security, Always{" "}
              <em className="italic font-sans">Protected</em>
            </h2>
          </motion.div>

          <motion.div {...fadeUp} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.7,
          ease: [0.2, 0.6, 0.2, 1],
          delay: prefersReducedMotion ? 0 : 0.08
        }} className="lg:col-span-5 mt-10 lg:mt-4">
            <div className="font-sans" style={{
            maxWidth: "480px",
            color: "var(--muted-foreground)",
            fontSize: "clamp(1rem, 1.05vw, 1.0625rem)",
            lineHeight: 1.65,
            letterSpacing: "0.005em"
          }}>
              <p className="mb-5">
                VPNGENIX provides robust security frameworks. Every protocol,
                encryption standard, and audit process strengthens your digital
                perimeter, ensuring your data remains safe in a volatile world.
              </p>
              <p className="mb-8">
                Threats are monitored in real-time; data is encrypted at rest;
                compliance is verified continuously. Our systems evolve daily,
                deliberately, so that your team can operate with total confidence
                in any environment.
              </p>
              <Link to="/CybersecurityCyberGenixPage" className="font-sans italic underline decoration-1 underline-offset-4 transition-colors duration-300 hover:text-[var(--primary)]" style={{
              color: "var(--foreground)",
              fontSize: "clamp(1rem, 1.05vw, 1.0625rem)"
            }}>
                View security standards
              </Link>
            </div>
          </motion.div>
        </div>

        {}
        <div className="mt-24 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-8">
          {cards.map((card, i) => <motion.article key={card.key} data-index={i} initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.7,
          ease: [0.2, 0.6, 0.2, 1],
          delay: prefersReducedMotion ? 0 : i * 0.08
        }} className="group relative flex flex-col" style={{
          backgroundColor: "var(--card)"
        }}>
              {}
              <div className="relative overflow-hidden" style={{
            aspectRatio: "3 / 4"
          }}>
                <img src={card.image.src} data-wvc-srcset={card.image.srcset} data-wvc-sizes={card.image.sizes} alt={card.image.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" />
              </div>

              <div className="flex flex-1 flex-col p-6 md:p-8">
                {}
                <p className="font-sans italic uppercase text-xs mb-5 flex items-center" style={{
              color: "var(--primary)",
              letterSpacing: "0.14em",
              fontVariant: "small-caps"
            }}>
                  <span aria-hidden="true" className="inline-block h-px w-6 mr-3" style={{
                backgroundColor: "var(--primary)"
              }} />
                  {card.eyebrow}
                </p>

                <h3 className="font-sans text-foreground mb-4" style={{
              fontSize: "clamp(1.15rem, 1.6vw, 1.5rem)",
              fontWeight: 600,
              letterSpacing: "-0.005em",
              lineHeight: 1.25
            }}>
                  {card.title}
                </h3>

                <p className="font-sans mb-8" style={{
              color: "var(--muted-foreground)",
              fontSize: "clamp(1rem, 1.05vw, 1.0625rem)",
              lineHeight: 1.65
            }}>
                  {card.description}
                </p>

                <div className="mt-auto pt-6">
                  <Link to="https://vpngenix.com/go/NordVPN" newTab className="group/link inline-flex items-center gap-2 font-sans uppercase text-xs text-foreground transition-colors duration-300 hover:text-[var(--primary)]" style={{
                letterSpacing: "0.14em"
              }}>
                    <span className="relative">
                      Learn More
                      <span aria-hidden="true" className="absolute left-0 -bottom-1.5 h-px w-full origin-right transition-transform duration-300 ease-out group-hover/link:scale-x-125" style={{
                    backgroundColor: "var(--primary)"
                  }} />
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/link:translate-x-1" strokeWidth={1} />
                  </Link>
                </div>
              </div>

              {}
              <span aria-hidden="true" className="absolute bottom-0 left-0 h-px w-full" style={{
            backgroundColor: "var(--border)"
          }} />
              <span aria-hidden="true" className="absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 transition-transform duration-[400ms] ease-out group-hover:scale-x-100" style={{
            backgroundColor: "var(--primary)"
          }} />
            </motion.article>)}
        </div>
      </div>
    </section>;
}